'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface ImpactStats {
  totalMilestones: number;
  activeCaretakers: number;
  questsCompleted: number;
  sharedWorks: number;
  citiesRepresented: number;
}

interface CohortData {
  id: string;
  name: string;
  name_arabic: string;
  role: string;
  memberCount: number;
  collectiveAlhamdulilah: number;
  topQuest: string;
  avgStage: number;
  goalProgress: number;
  goalTarget: number;
}

interface TimelinePoint {
  date: string;
  count: number;
}

interface RoleProgress {
  role: string;
  label: string;
  icon: string;
  stages: number[]; // percentage of members at each stage
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const RECOGNITION_STAGES = ['Mata', 'Encounter', 'Examination', 'Recognition', 'Re-Assembly', 'Uboor'];

const ROLE_LABELS: Record<string, { label: string; icon: string }> = {
  'inner-seeker': { label: 'Inner Seekers', icon: '◎' },
  'community-caretaker': { label: 'Community Caretakers', icon: '⬡' },
  'academic': { label: 'Academics', icon: '✦' },
  'activist': { label: 'Activists', icon: '⌘' },
  'explorer': { label: 'Explorers', icon: '◈' },
  'local-resource': { label: 'Local Resources', icon: '⬡' },
};

const COHORT_COLORS: Record<string, string> = {
  'inner-seeker': '#8a8ab8',
  'community-caretaker': '#2d8a87',
  'academic': '#c9a84c',
  'activist': '#2d7a47',
};

// ─── ANIMATED COUNTER ─────────────────────────────────────────────────────────

function AnimatedCounter({ target, duration = 1500 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === 0) return;
    startRef.current = null;

    const animate = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return <>{count.toLocaleString()}</>;
}

// ─── SVG TIMELINE CHART ───────────────────────────────────────────────────────

function TimelineChart({ data }: { data: TimelinePoint[] }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const width = 800;
  const height = 160;
  const padX = 40;
  const padY = 20;

  if (data.length < 2) {
    return (
      <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 12, fontFamily: 'monospace' }}>Gathering timeline data...</p>
      </div>
    );
  }

  const maxVal = Math.max(...data.map(d => d.count), 1);
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;

  const points = data.map((d, i) => ({
    x: padX + (i / (data.length - 1)) * chartW,
    y: padY + chartH - (d.count / maxVal) * chartH,
    ...d,
  }));

  // Bezier path
  const pathD = points.reduce((acc, pt, i) => {
    if (i === 0) return `M ${pt.x} ${pt.y}`;
    const prev = points[i - 1];
    const cpX = (prev.x + pt.x) / 2;
    return `${acc} C ${cpX} ${prev.y}, ${cpX} ${pt.y}, ${pt.x} ${pt.y}`;
  }, '');

  const areaD = `${pathD} L ${points[points.length - 1].x} ${padY + chartH} L ${points[0].x} ${padY + chartH} Z`;

  return (
    <div style={{ position: 'relative', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 160 }}>
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c9a84c" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#c9a84c" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Area fill */}
        <path d={areaD} fill="url(#areaGrad)" />
        {/* Line */}
        <path d={pathD} fill="none" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" />
        {/* Data points */}
        {points.map((pt, i) => (
          <g key={i}>
            <circle
              cx={pt.x} cy={pt.y} r={hovered === i ? 5 : 3}
              fill={hovered === i ? '#c9a84c' : '#c9a84c'}
              opacity={hovered === i ? 1 : 0.6}
              style={{ cursor: 'pointer', transition: 'r 150ms' }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
            {hovered === i && (
              <g>
                <rect x={pt.x - 50} y={pt.y - 36} width={100} height={28} rx={4} fill="rgba(6,13,18,0.95)" stroke="rgba(201,168,76,0.3)" strokeWidth={1} />
                <text x={pt.x} y={pt.y - 22} textAnchor="middle" fill="#c9a84c" fontSize={10} fontFamily="monospace">{pt.count} الحمد لله</text>
                <text x={pt.x} y={pt.y - 12} textAnchor="middle" fill="rgba(212,197,169,0.5)" fontSize={8} fontFamily="monospace">{pt.date}</text>
              </g>
            )}
          </g>
        ))}
        {/* X axis labels (every 7 days) */}
        {points.filter((_, i) => i % 7 === 0).map((pt, i) => (
          <text key={i} x={pt.x} y={height - 4} textAnchor="middle" fill="rgba(212,197,169,0.3)" fontSize={8} fontFamily="monospace">
            {pt.date.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────

function AnimatedProgressBar({ progress, color = '#c9a84c' }: { progress: number; color?: string }) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setWidth(Math.min(100, progress)), 100);
    return () => clearTimeout(t);
  }, [progress]);

  return (
    <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${width}%`, background: color, borderRadius: 3, transition: 'width 1s ease' }} />
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function ImpactPage() {
  const [stats, setStats] = useState<ImpactStats>({ totalMilestones: 0, activeCaretakers: 0, questsCompleted: 0, sharedWorks: 0, citiesRepresented: 0 });
  const [cohorts, setCohorts] = useState<CohortData[]>([]);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [roleProgress, setRoleProgress] = useState<RoleProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Parallel fetch all stats
      const [
        milestonesRes,
        caretakersRes,
        questsRes,
        sharesRes,
        citiesRes,
        cohortsRes,
        timelineRes,
        walkthroughRes,
      ] = await Promise.all([
        supabase.from('walkthrough_completions').select('id', { count: 'exact', head: true }),
        supabase.from('community_signups').select('id', { count: 'exact', head: true }),
        supabase.from('caretaker_quests').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
        supabase.from('community_shares').select('id', { count: 'exact', head: true }),
        supabase.from('community_signups').select('city'),
        supabase.from('role_cohorts').select('*'),
        supabase.from('walkthrough_completions').select('completed_at').order('completed_at', { ascending: true }),
        supabase.from('walkthrough_completions').select('caretaker_role, checkpoint_id'),
      ]);

      const cities = new Set((citiesRes.data || []).map((r: any) => r.city).filter(Boolean));

      setStats({
        totalMilestones: milestonesRes.count || 0,
        activeCaretakers: caretakersRes.count || 0,
        questsCompleted: questsRes.count || 0,
        sharedWorks: sharesRes.count || 0,
        citiesRepresented: cities.size,
      });

      // Build cohort data
      const cohortDefs = cohortsRes.data || [];
      const cohortData: CohortData[] = await Promise.all(
        cohortDefs.map(async (c: any) => {
          const { count: memberCount } = await supabase
            .from('cohort_memberships')
            .select('id', { count: 'exact', head: true })
            .eq('role', c.role);

          const { data: topQuestData } = await supabase
            .from('caretaker_quests')
            .select('title')
            .eq('caretaker_role', c.role)
            .eq('status', 'active')
            .limit(1);

          return {
            id: c.id,
            name: c.name,
            name_arabic: c.name_arabic,
            role: c.role,
            memberCount: memberCount || 0,
            collectiveAlhamdulilah: (memberCount || 0) * 12, // estimated
            topQuest: topQuestData?.[0]?.title || 'Witnessing the Signs',
            avgStage: 2,
            goalProgress: Math.min(100, Math.round(((memberCount || 0) / (c.goal_target || 20)) * 100)),
            goalTarget: c.goal_target || 20,
          };
        })
      );
      setCohorts(cohortData);

      // Build timeline (last 30 days)
      const now = new Date();
      const days: TimelinePoint[] = [];
      const completions = timelineRes.data || [];

      for (let i = 29; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const dateStr = d.toISOString().slice(0, 10);
        const count = completions.filter((c: any) => c.completed_at?.slice(0, 10) === dateStr).length;
        days.push({ date: dateStr, count });
      }
      setTimeline(days);

      // Build role progress
      const walkthroughData = walkthroughRes.data || [];
      const roles = Object.keys(ROLE_LABELS);
      const rp: RoleProgress[] = roles.map(role => {
        const roleData = walkthroughData.filter((w: any) => w.caretaker_role === role);
        const total = roleData.length || 1;
        const stages = RECOGNITION_STAGES.map((_, si) => {
          const atStage = roleData.filter((w: any) => {
            const stageNum = parseInt(w.checkpoint_id || '0');
            return stageNum >= si;
          }).length;
          return Math.round((atStage / total) * 100);
        });
        return {
          role,
          label: ROLE_LABELS[role]?.label || role,
          icon: ROLE_LABELS[role]?.icon || '◎',
          stages,
        };
      });
      setRoleProgress(rp);

    } catch (err) {
      console.error('Impact data error:', err);
    }
    setLoading(false);
  }, [supabase]);

  useEffect(() => { loadData(); }, [loadData]);

  // Real-time subscription for live counters
  useEffect(() => {
    const channel = supabase
      .channel('impact-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'walkthrough_completions' }, loadData)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'community_signups' }, loadData)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [supabase, loadData]);

  const communityGoals = [
    { title: '100 Active Caretakers', titleAr: 'مئة قائم', current: stats.activeCaretakers, target: 100, icon: '⬡' },
    { title: 'All 9 Cities Represented', titleAr: 'تسع مدن', current: stats.citiesRepresented, target: 9, icon: '◎' },
    { title: '500 Quests Completed', titleAr: 'خمسمئة مهمة', current: stats.questsCompleted, target: 500, icon: '⌘' },
    { title: '50 Shared Garments', titleAr: 'خمسون ثوباً', current: stats.sharedWorks, target: 50, icon: '✦' },
  ];

  function getGoalBadge(progress: number) {
    if (progress >= 100) return { label: 'الحمد لله — Achieved', color: '#2d7a47' };
    if (progress >= 50) return { label: 'قريباً — Approaching', color: '#D4A82A' };
    return { label: 'جاري العمل — In Progress', color: '#2d8a87' };
  }

  return (
    <div style={{ minHeight: '100vh', background: '#060D12', color: '#F5F0E8' }}>
      <Header />
      <Breadcrumb items={[{ label: 'Community', href: '/community' }, { label: 'Impact' }]} />
      <main style={{ width: '100%', maxWidth: '100%' }}>

        {/* ── SECTION 1: HERO ─────────────────────────────────────────────── */}
        <section
          style={{
            background: 'linear-gradient(180deg, #0a1a12 0%, #060d12 100%)',
            borderBottom: '1px solid rgba(201,168,76,0.1)',
            paddingTop: 'clamp(80px, 15vw, 120px)',
            paddingBottom: 'clamp(48px, 8vw, 80px)',
          }}
        >
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(16px, 4vw, 32px)', textAlign: 'center' }}>
            <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(201,168,76,0.5)' }}>
              ◈ Caretaker Network
            </p>
            <h1
              style={{
                fontFamily: "'Amiri', serif",
                fontSize: 'clamp(28px, 6vw, 64px)',
                color: '#c9a84c',
                lineHeight: 1.1,
                marginBottom: 16,
                background: 'linear-gradient(135deg, #c9a84c 0%, #f0d080 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                overflowWrap: 'break-word',
              }}
            >
              CARETAKER IMPACT · أثر القائمين
            </h1>
            <p
              style={{
                fontFamily: "'Amiri', serif",
                fontSize: 'clamp(14px, 3vw, 20px)',
                color: 'rgba(212,197,169,0.7)',
                marginBottom: 'clamp(32px, 6vw, 60px)',
                direction: 'rtl',
                overflowWrap: 'break-word',
              }}
            >
              الحمد لله — Every recognition, every quest, every connection counts.
            </p>

            {/* Live Counters */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 max-w-4xl mx-auto">
              {[
                { label: 'Alhamdulilah Milestones', labelAr: 'الحمد لله', value: stats.totalMilestones, color: '#c9a84c' },
                { label: 'Active Caretakers', labelAr: 'القائمون', value: stats.activeCaretakers, color: '#2d8a87' },
                { label: 'Quests Completed', labelAr: 'المهام', value: stats.questsCompleted, color: '#D4A82A' },
                { label: 'Shared Works', labelAr: 'الأعمال', value: stats.sharedWorks, color: '#2d7a47' },
                { label: 'Cities', labelAr: 'المدن', value: stats.citiesRepresented, color: '#8a8ab8' },
              ].map(stat => (
                <div key={stat.label} className="text-center">
                  <div
                    style={{
                      fontFamily: "'Amiri', serif",
                      fontSize: 'clamp(28px, 4vw, 48px)',
                      color: stat.color,
                      fontWeight: 700,
                      lineHeight: 1,
                      marginBottom: 6,
                    }}
                  >
                    {loading ? '—' : <AnimatedCounter target={stat.value} />}
                  </div>
                  <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.5)' }}>
                    {stat.label}
                  </p>
                  <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${stat.color}60`, marginTop: 2 }}>
                    {stat.labelAr}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 2: COHORT PROGRESS ──────────────────────────────────── */}
        <section style={{ background: '#f5f0e8', padding: '80px 0' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.6)' }}>
                Community
              </p>
              <h2
                className="font-mono text-2xl font-black uppercase tracking-tight mb-2"
                style={{ color: '#1C2B25' }}
              >
                COHORTS · المجموعات
              </h2>
              <p style={{ color: 'rgba(28,43,37,0.5)', fontSize: 14 }}>
                Role-based caretaker cohorts and their collective journey.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(cohorts.length > 0 ? cohorts : [
                { id: '1', name: 'Inner Seekers', name_arabic: 'الباحثون الداخليون', role: 'inner-seeker', memberCount: 0, collectiveAlhamdulilah: 0, topQuest: 'Witnessing the Signs', avgStage: 1, goalProgress: 0, goalTarget: 50 },
                { id: '2', name: 'Community Caretakers', name_arabic: 'القائمون على المجتمع', role: 'community-caretaker', memberCount: 0, collectiveAlhamdulilah: 0, topQuest: 'Community Connection', avgStage: 1, goalProgress: 0, goalTarget: 20 },
                { id: '3', name: 'Academics', name_arabic: 'الأكاديميون', role: 'academic', memberCount: 0, collectiveAlhamdulilah: 0, topQuest: 'Framework Reflection', avgStage: 1, goalProgress: 0, goalTarget: 10 },
                { id: '4', name: 'Activists', name_arabic: 'الناشطون', role: 'activist', memberCount: 0, collectiveAlhamdulilah: 0, topQuest: 'Justice Documentation', avgStage: 1, goalProgress: 0, goalTarget: 15 },
              ]).map(cohort => {
                const color = COHORT_COLORS[cohort.role] || '#c9a84c';
                const avgStageLabel = RECOGNITION_STAGES[Math.min(cohort.avgStage - 1, 5)] || 'Mata';
                return (
                  <div
                    key={cohort.id}
                    className="rounded-xl p-6"
                    style={{ background: 'white', border: `1px solid ${color}20`, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#1C2B25' }}>
                          {cohort.name}
                        </h3>
                        <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${color}80`, direction: 'rtl' }}>
                          {cohort.name_arabic}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-2xl font-black" style={{ color }}>{cohort.memberCount}</p>
                        <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(28,43,37,0.4)' }}>members</p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between items-center">
                        <span style={{ color: 'rgba(28,43,37,0.5)', fontSize: 11 }}>Collective الحمد لله</span>
                        <span className="font-mono text-sm font-black" style={{ color }}>{cohort.collectiveAlhamdulilah}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: 'rgba(28,43,37,0.5)', fontSize: 11 }}>Top Quest</span>
                        <span style={{ color: '#1C2B25', fontSize: 11, maxWidth: 160, textAlign: 'right' }} className="truncate">{cohort.topQuest}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: 'rgba(28,43,37,0.5)', fontSize: 11 }}>Avg Stage</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: `${color}12`, color }}>{avgStageLabel}</span>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(28,43,37,0.4)' }}>Cohort Goal</span>
                        <span className="font-mono text-[8px]" style={{ color: 'rgba(28,43,37,0.4)' }}>{cohort.goalProgress}%</span>
                      </div>
                      <AnimatedProgressBar progress={cohort.goalProgress} color={color} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── SECTION 3: ALHAMDULILAH TIMELINE ───────────────────────────── */}
        <section style={{ background: '#0a1a12', padding: '80px 0' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(201,168,76,0.4)' }}>
                30-Day View
              </p>
              <h2
                className="font-mono text-2xl font-black uppercase tracking-tight mb-2"
                style={{ color: '#c9a84c' }}
              >
                ALHAMDULILAH TIMELINE · سجل الحمد لله
              </h2>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 14 }}>
                Milestones reached across the caretaker network over time.
              </p>
            </div>

            <div
              className="rounded-xl p-6"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.1)' }}
            >
              <TimelineChart data={timeline} />

              {/* Key milestone moments */}
              {timeline.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {[
                    { label: 'First Caretaker Joined', value: timeline[0]?.date || '—', icon: '◎', color: '#2d8a87' },
                    { label: 'Most Active Day', value: timeline.reduce((best, d) => d.count > best.count ? d : best, timeline[0])?.date || '—', icon: '✦', color: '#c9a84c' },
                    { label: 'Total This Month', value: `${timeline.reduce((s, d) => s + d.count, 0)} الحمد لله`, icon: '⌘', color: '#D4A82A' },
                  ].map(m => (
                    <div key={m.label} className="flex items-center gap-3 p-4 rounded-lg" style={{ background: `${m.color}08`, border: `1px solid ${m.color}20` }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${m.color}15`, border: `1px solid ${m.color}30`, color: m.color }}>
                        {m.icon}
                      </div>
                      <div>
                        <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>{m.label}</p>
                        <p className="font-mono text-xs font-bold" style={{ color: m.color }}>{m.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── SECTION 4: SHARED WORK VOLUME ───────────────────────────────── */}
        <section style={{ background: '#f5f0e8', padding: '80px 0' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.6)' }}>
                Community Output
              </p>
              <h2 className="font-mono text-2xl font-black uppercase tracking-tight mb-2" style={{ color: '#1C2B25' }}>
                SHARED WORKS · الأعمال المشتركة
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {[
                { title: 'Shared Quests', titleAr: 'المهام المشتركة', value: stats.questsCompleted, sub: 'Most shared: Witnessing the Signs', icon: '⌘', color: '#D4A82A' },
                { title: 'Featured Garments', titleAr: 'الثياب المميزة', value: stats.sharedWorks, sub: 'Most resonant: Community Caretaker pattern', icon: '⬡', color: '#2d8a87' },
                { title: 'Community Connections', titleAr: 'الروابط', value: stats.activeCaretakers, sub: 'Most active: Inner Seekers cohort', icon: '◎', color: '#8a8ab8' },
              ].map(card => (
                <div key={card.title} className="rounded-xl p-6" style={{ background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: `1px solid ${card.color}15` }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${card.color}12`, border: `1px solid ${card.color}25`, color: card.color, fontSize: 16 }}>
                      {card.icon}
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(28,43,37,0.4)' }}>{card.title}</p>
                      <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${card.color}70` }}>{card.titleAr}</p>
                    </div>
                  </div>
                  <p className="font-mono text-3xl font-black mb-2" style={{ color: card.color }}>
                    {loading ? '—' : <AnimatedCounter target={card.value} />}
                  </p>
                  <p style={{ color: 'rgba(28,43,37,0.5)', fontSize: 11 }}>{card.sub}</p>
                </div>
              ))}
            </div>

            {/* Trending Framework Concepts Tag Cloud */}
            <div className="rounded-xl p-6" style={{ background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(28,43,37,0.4)' }}>
                TRENDING FRAMEWORK CONCEPTS
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Al-Basir', type: 'name', size: 16 },
                  { label: 'Al-Khabir', type: 'name', size: 14 },
                  { label: 'Mizan', type: 'key', size: 13 },
                  { label: 'Haal', type: 'glossary', size: 15 },
                  { label: 'Al-Qadir', type: 'name', size: 12 },
                  { label: 'Amanah', type: 'glossary', size: 14 },
                  { label: 'Sunnat Allah', type: 'key', size: 11 },
                  { label: 'Khalifa', type: 'glossary', size: 13 },
                  { label: 'Islah', type: 'glossary', size: 12 },
                  { label: 'Al-Muhyi', type: 'name', size: 11 },
                  { label: 'Haqq', type: 'glossary', size: 16 },
                  { label: 'IRL Lens', type: 'key', size: 13 },
                  { label: 'Al-Razzaq', type: 'name', size: 10 },
                  { label: 'Uboor', type: 'glossary', size: 11 },
                ].map(tag => {
                  const color = tag.type === 'name' ? '#c9a84c' : tag.type === 'key' ? '#2d7a47' : '#2d8a87';
                  return (
                    <span
                      key={tag.label}
                      className="font-mono uppercase tracking-widest px-3 py-1 rounded-full"
                      style={{
                        fontSize: tag.size,
                        background: `${color}10`,
                        border: `1px solid ${color}25`,
                        color,
                      }}
                    >
                      {tag.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 5: ROLES COLLECTIVE PROGRESS ────────────────────────── */}
        <section style={{ background: '#0a1a12', padding: '80px 0' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(201,168,76,0.4)' }}>
                Recognition Arc
              </p>
              <h2 className="font-mono text-2xl font-black uppercase tracking-tight mb-2" style={{ color: '#F5F0E8' }}>
                COLLECTIVE JOURNEY · الرحلة الجماعية
              </h2>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 14 }}>
                Where each role's caretakers are on the recognition arc.
              </p>
            </div>

            <div className="space-y-4">
              {(roleProgress.length > 0 ? roleProgress : Object.entries(ROLE_LABELS).map(([role, info]) => ({
                role,
                label: info.label,
                icon: info.icon,
                stages: [80, 60, 40, 25, 10, 5],
              }))).map(rp => (
                <div
                  key={rp.role}
                  className="rounded-xl p-5"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <div className="flex items-center gap-4 flex-wrap">
                    {/* Role label */}
                    <div className="flex items-center gap-2 w-40 flex-shrink-0">
                      <span style={{ color: '#c9a84c', fontSize: 14 }}>{rp.icon}</span>
                      <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.7)' }}>{rp.label}</p>
                    </div>

                    {/* Stage dots */}
                    <div className="flex items-center gap-2 flex-1">
                      {RECOGNITION_STAGES.map((stage, si) => {
                        const pct = rp.stages[si] || 0;
                        const isFull = pct > 20;
                        const isHalf = pct >= 5 && pct <= 20;
                        return (
                          <div key={stage} className="flex flex-col items-center gap-1">
                            <div
                              title={`${stage}: ${pct}%`}
                              style={{
                                width: 20, height: 20, borderRadius: '50%',
                                background: isFull ? '#c9a84c' : isHalf ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.06)',
                                border: `2px solid ${isFull ? '#c9a84c' : isHalf ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.1)'}`,
                                boxShadow: isFull ? '0 0 8px rgba(201,168,76,0.4)' : 'none',
                              }}
                            />
                            <span className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.25)' }}>
                              {stage.slice(0, 3)}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Completion % */}
                    <div className="text-right w-16 flex-shrink-0">
                      <p className="font-mono text-sm font-black" style={{ color: '#c9a84c' }}>
                        {rp.stages[rp.stages.length - 1] || 0}%
                      </p>
                      <p className="font-mono text-[7px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>Uboor</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 6: COMMUNITY GOALS ──────────────────────────────────── */}
        <section style={{ background: '#f5f0e8', padding: '80px 0' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.6)' }}>
                Collective Work
              </p>
              <h2 className="font-mono text-2xl font-black uppercase tracking-tight mb-2" style={{ color: '#1C2B25' }}>
                COMMUNITY GOALS · أهداف المجتمع
              </h2>
              <p style={{ color: 'rgba(28,43,37,0.5)', fontSize: 14 }}>
                What we are collectively working toward.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {communityGoals.map(goal => {
                const progress = Math.min(100, Math.round((goal.current / goal.target) * 100));
                const badge = getGoalBadge(progress);
                return (
                  <div
                    key={goal.title}
                    className="rounded-xl p-6"
                    style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(28,43,37,0.06)' }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span style={{ color: '#c9a84c', fontSize: 16 }}>{goal.icon}</span>
                          <h3 className="font-mono text-xs font-black uppercase tracking-wide" style={{ color: '#1C2B25' }}>
                            {goal.title}
                          </h3>
                        </div>
                        <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(28,43,37,0.4)', direction: 'rtl' }}>
                          {goal.titleAr}
                        </p>
                      </div>
                      <span
                        className="font-mono text-[7px] uppercase tracking-widest px-2 py-1 rounded"
                        style={{ background: `${badge.color}12`, border: `1px solid ${badge.color}25`, color: badge.color, whiteSpace: 'nowrap' }}
                      >
                        {badge.label}
                      </span>
                    </div>

                    <div className="mb-3">
                      <AnimatedProgressBar progress={progress} color="#c9a84c" />
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="font-mono text-xs font-black" style={{ color: '#c9a84c' }}>
                        {loading ? '—' : <AnimatedCounter target={goal.current} />}
                      </span>
                      <span className="font-mono text-[9px]" style={{ color: 'rgba(28,43,37,0.4)' }}>
                        / {goal.target}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer CTA */}
            <div className="text-center pt-6" style={{ borderTop: '1px solid rgba(28,43,37,0.08)' }}>
              <p style={{ color: 'rgba(28,43,37,0.5)', fontSize: 13, marginBottom: 20 }}>
                These goals are updated in real time as caretakers engage with the platform. Join the journey.
              </p>
              <Link
                href="/community#signup"
                className="inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase px-8 py-4 transition-all duration-200"
                style={{ background: '#1C2B25', color: '#c9a84c', borderRadius: 4 }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#2d3f35'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#1C2B25'; }}
              >
                Join the Caretakers →
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
